import { MAX_FORM_FIELD_WALK_UP } from "@protontech/autofill/constants/heuristics";
import { formCandidateSelector, kInputIteratorSelector } from "@protontech/autofill/constants/selectors";
import { walkUpWhile } from "./dom";
import type { HTMLFieldElement } from "./field";
import { attrIgnored, isIgnored } from "./flags";
import { isVisibleField } from "./visible";

/* Some form elements - notably submit buttons - can be located
 * outside of the form element we're currently running the feature
 * pipeline on. Heuristics have shown little disturbance in the
 * model's precision when trying to resolve a parent element as
 * long as it doesn't contain any other form */
export const getFormParent = (form: HTMLElement) => walkUpWhile(form, MAX_FORM_FIELD_WALK_UP)((el) => el.querySelectorAll(formCandidateSelector).length <= 1);

export interface FormInputIterator {
  prev: (el: HTMLElement, tagNameFilter?: string) => HTMLElement | null;
  next: (el: HTMLElement, tagNameFilter?: string) => HTMLElement | null;
}

export const createInputIterator = (form: HTMLElement) => {
  const formEls = Array.from(form.querySelectorAll<HTMLElement>(kInputIteratorSelector)).filter(isVisibleField);

  const getAdjacent = (el: HTMLElement, offset: number, tagNameFilter?: string): HTMLElement | null => {
    const idx = formEls.indexOf(el);
    const res = idx === -1 ? null : (formEls?.[idx + offset] ?? null);
    return res && tagNameFilter && res?.tagName !== tagNameFilter ? getAdjacent(res, offset, tagNameFilter) : res;
  };

  const iterator: FormInputIterator = {
    prev: (el, tagNameFilter) => getAdjacent(el, -1, tagNameFilter),
    next: (el, tagNameFilter) => getAdjacent(el, 1, tagNameFilter),
  };

  return iterator;
};

export const selectFormCandidates = (root: Document | HTMLElement = document) => {
  const candidates = Array.from(root.querySelectorAll<HTMLElement>(formCandidateSelector));
  return candidates.filter((form) => !isIgnored(form) && !attrIgnored(form));
};

type FormComplexityOptions = {
  fields: HTMLFieldElement[];
  visibleFields: number;
  nonVisibleFields: number;
  hiddenFields: number;
  buttons: number;
  anchors: number;
};

/** Computes a weighted complexity score for form classification.
 * Rationale: Simple forms (login) should score low, complex forms (registration,
 * checkout) should score high. The score helps distinguish form types when used
 * alongside other features - notably, login forms should exhibit negative weights
 * on complexity while complex forms like sign-ups should saturate at higher values.*/
export const getFormComplexity = (form: HTMLElement, options: FormComplexityOptions): number => {
  const types = new Set<string>();
  let score = 0;

  /** Core interactive elements - primary signal */
  score += options.visibleFields * 2;
  score += options.buttons * 1;
  score += options.anchors * 0.5;
  score += options.nonVisibleFields * 0.25;
  score += options.hiddenFields * 0.1;

  for (const field of options.fields) {
    const type = field.tagName === "INPUT" ? field.type : field.tagName;
    if (!types.has(type)) {
      score += 0.2;
      types.add(type);
    }
  }

  /** Semantic groupings - indicates structured/sectioned forms */
  score += form.querySelectorAll("fieldset, [role='group'], [role='radiogroup']").length * 0.2;

  /** Container density - captures layout complexity. */
  for (const el of form.querySelectorAll("div, section")) {
    if (el.children.length > 1) score += 0.2;
  }

  return score;
};
